import { useMemo } from "react"
import { useField } from "formik"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { NullableCalendarEvent } from "../../resource/calendar/fullCalendarTypes"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { ResourcesListInfo } from "../../resource/ResourcesListInfo"
import { isOngoing } from "../../resource/calendar/helpers"
import { ResourceItem } from "../../../createOrderTypes"
import { OrderSummaryItem } from "./OrderSummaryItem"
import { OrderSummaryItemBox } from "./OrderSummaryItemBox"

type ResourcesProps = Readonly<{
    isUpdatable: boolean
}>

export function Resources({ isUpdatable }: ResourcesProps) {
    const dateTimeFactory = useDateTime()

    const [{ value: event }, , eventHelpers] = useField<NullableCalendarEvent>("event")
    const [{ value: fullResources }, , fullResourcesHelpers] = useField<ResourceItem[]>("fullResourcesList")

    const bookingTime = useMemo(() => {
        if (!event) return null

        const start = dateTimeFactory.fromISO(event.start)
        const end = dateTimeFactory.fromISO(event.end)

        if (!start?.isValid || !end?.isValid) return null

        return start.hasSame(end, "day")
            ? `${start.toFormat("d MMMM")}, ${start.toFormat("t")} - ${end.toFormat("t")}`
            : `${start.toFormat("d MMMM t")} - ${end.toFormat("d MMMM t")}`
    }, [event, dateTimeFactory])

    const canEditResources = Boolean(
        isUpdatable && event && !isOngoing({ start: event.start, end: event.end }, dateTimeFactory),
    )

    const handleRemove = (id: number) => {
        if (!event) return

        const updatedResourceIds = event.resourceIds.filter((rId) => +rId !== id)

        if (updatedResourceIds.length === 0) {
            eventHelpers.setValue(null)
            fullResourcesHelpers.setValue([])
            return
        }

        const updatedFullResources = fullResources.filter((res) => updatedResourceIds.includes(res.id.toString()))

        eventHelpers.setValue({ ...event, resourceIds: updatedResourceIds })
        fullResourcesHelpers.setValue(updatedFullResources)
    }

    return (
        <LocalizedStrict id="create-order-order-summary-resources" attrs={{ title: true }}>
            <OrderSummaryItem title="Resources">
                <OrderSummaryItemBox
                    title={
                        <LocalizedStrict id="create-order-order-summary-resources-booking-time">
                            <>Booking</>
                        </LocalizedStrict>
                    }
                    titleDetails={bookingTime}
                >
                    <ResourcesListInfo
                        resourcesList={fullResources}
                        showResourcesType={false}
                        {...(canEditResources && { handleDeleteResource: handleRemove })}
                        isRedIcon={false}
                    />
                </OrderSummaryItemBox>
            </OrderSummaryItem>
        </LocalizedStrict>
    )
}
